// Festival time, which is not calendar time.
//
// A day runs until its rollover hour, so a 01:30 set belongs to the night before.
// Hardcoding midnight produces a grid where the last two hours of every night are
// filed under the wrong tab, which is the sort of bug nobody reports as a bug --
// they just stop trusting the schedule.

import type { Day, FestivalSet } from './types.ts';

export const MINUTE = 60 * 1000;
export const HOUR = 60 * MINUTE;

/** The venue's zone. Every grid label and every exported instant uses it. */
export const VENUE_TIMEZONE = 'America/New_York';

const zoneFormatter = (zone: string) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });

function zoneParts(formatter: Intl.DateTimeFormat, ts: number): Record<string, string> {
  return Object.fromEntries(
    formatter
      .formatToParts(ts)
      .filter((p) => p.type !== 'literal')
      .map((p) => [p.type, p.value]),
  );
}

// The instant a civil date and time names in the venue's zone. Offsets are
// searched in fifteen-minute steps, which covers every modern zone. A civil
// time skipped by a spring-forward jump throws, and so does one a fall-back
// repeats, rather than either resolving to a silently chosen instant. A Date
// built from local components gives a different instant on every machine, and a
// client and a Worker then disagree about when a set starts.
export function venueTimestamp(date: string, time: string, zone = VENUE_TIMEZONE): number {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) throw new Error('format');
  const [y, m, d] = date.split('-').map(Number);
  const [h, min] = time.split(':').map(Number);
  const utc = Date.UTC(y, m - 1, d, h, min);
  if (h > 23 || min > 59 || new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10) !== date) {
    throw new Error('date');
  }

  const formatter = zoneFormatter(zone);
  const candidates: number[] = [];
  for (let offset = -14 * 60; offset <= 14 * 60; offset += 15) {
    const ts = utc - offset * 60000;
    const p = zoneParts(formatter, ts);
    if (`${p.year}-${p.month}-${p.day}` === date && `${p.hour}:${p.minute}` === time) {
      candidates.push(ts);
    }
  }
  if (candidates.length !== 1) {
    throw new Error(candidates.length ? 'ambiguous_time' : 'nonexistent_time');
  }
  return candidates[0];
}

// The same conversion with the festival-night rollover applied to the date, so
// a small-hours set listed under one night resolves to the following morning.
export function festivalTimestamp(
  date: string,
  time: string,
  zone = VENUE_TIMEZONE,
  rolloverHour = 6,
): number {
  if (!/^\d{2}:\d{2}$/.test(time) || !Number.isInteger(rolloverHour) || rolloverHour < 0 || rolloverHour > 23) {
    throw new Error('time');
  }
  venueTimestamp(date, '12:00', zone);
  const day =
    Number(time.slice(0, 2)) < rolloverHour
      ? new Date(Date.parse(`${date}T12:00:00Z`) + 86400000).toISOString().slice(0, 10)
      : date;
  return venueTimestamp(day, time, zone);
}

export function formatVenueTime(ts: number, zone = VENUE_TIMEZONE): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour: 'numeric',
    minute: '2-digit',
  }).format(ts);
}

/** The calendar date a timestamp falls on in the venue's zone. */
export function venueDateString(ts: number, zone = VENUE_TIMEZONE): string {
  const p = zoneParts(zoneFormatter(zone), ts);
  return `${p.year}-${p.month}-${p.day}`;
}

/**
 * The calendar date a timestamp is filed under, given the rollover hour.
 * Returns an ISO date string (YYYY-MM-DD) in the supplied timezone offset-free
 * local frame; callers work in one festival timezone throughout.
 */
export function festivalDateOf(ts: number, rolloverHour: number, zone = VENUE_TIMEZONE): string {
  return venueDateString(ts - rolloverHour * HOUR, zone);
}

export function toDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Start and end of a festival day as timestamps: [rollover, next rollover). */
export function dayBounds(day: Day, zone = VENUE_TIMEZONE): { start: number; end: number } {
  const start = venueTimestamp(
    day.calendarDate,
    `${String(day.rolloverHour).padStart(2, '0')}:00`,
    zone,
  );
  return { start, end: start + 24 * HOUR };
}

export function dayIdFor(days: Day[], ts: number): string | undefined {
  for (const day of days) {
    const { start, end } = dayBounds(day);
    if (ts >= start && ts < end) return day.id;
  }
  return undefined;
}

/** Assigns each set to the festival day that contains its start. */
export function assignSetsToDays(sets: FestivalSet[], days: Day[]): FestivalSet[] {
  return sets.map((s) => {
    const dayId = dayIdFor(days, s.startTs);
    return dayId && dayId !== s.dayId ? { ...s, dayId } : s;
  });
}

// Labels read in the venue's zone rather than the reader's, so a phone still
// set to another zone shows the time printed on the wristband.
export function fmtTime(ts: number, zone = VENUE_TIMEZONE): string {
  const p = zoneParts(zoneFormatter(zone), ts);
  const h = Number(p.hour);
  const suffix = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${p.minute}${suffix}`;
}

export function fmtRange(startTs: number, endTs: number): string {
  return `${fmtTime(startTs)} to ${fmtTime(endTs)}`;
}

export function minutesBetween(a: number, b: number): number {
  return Math.round((b - a) / MINUTE);
}

/** Overlap of two intervals in milliseconds, zero when they do not touch. */
export function overlapMs(aStart: number, aEnd: number, bStart: number, bEnd: number): number {
  return Math.max(0, Math.min(aEnd, bEnd) - Math.max(aStart, bStart));
}
